import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useUIContext } from "@/contexts/UIContext";
import { projectQuery } from "@/lib/projectsQuery";
import { isProjectType } from "@/lib/projectType";
import { readProjectsSnapshot } from "@/lib/projectsSnapshot";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * Whether the project is High-rise — suites in buildings — rather than lots
 * (low-rise, towns, stacked). Reads the already-cached project query; until
 * that answers, the device's copy of the project list says, so a label that
 * depends on it ("Lots" or "Suites" in the menu) is right on first paint
 * instead of switching under the reader. The copy is only drawn from, never
 * read as data (lib/projectsSnapshot.ts).
 */
export function useIsHighRise(projectIdOverride?: string): boolean {
  const type = useProjectType(projectIdOverride);
  return isProjectType(type, "High-rise");
}

/** Whether the project is a Company project (SaleFish's own, a brokerage's): no homes to sell, maybe. Read as useIsHighRise is. */
export function useIsCompanyProject(projectIdOverride?: string): boolean {
  const type = useProjectType(projectIdOverride);
  return isProjectType(type, "Company");
}

function useProjectType(projectIdOverride?: string): string | null {
  const { projectId: ctxProjectId } = useUIContext();
  const projectId = projectIdOverride ?? ctxProjectId;

  const { data: project } = useQuery<{ projectType?: string } | null>(projectQuery(projectId));

  const deviceType = useMemo(
    () => (project || !projectId ? null : readProjectsSnapshot()?.projects.find((p) => p.id === projectId)?.projectType ?? null),
    [project, projectId],
  );

  return project?.projectType ?? deviceType;
}

/**
 * Returns the correct terminology for the "building/phase" entity based on the
 * active project's type:
 *   - High-rise           → "Building/Phase"
 *   - Everything else      → "Phase"  (low-rise, towns, stacked, etc.)
 *
 * Centralises the label so every inventory surface stays consistent. Reads the
 * already-cached project query, so it adds no extra network cost.
 *
 * The term is a LABEL on its own (a field, a column, a heading). It is never
 * slotted into a sentence: "Add a {term}" reads stiffly wherever an article or
 * adjective agrees with the noun's gender (French, Spanish, Italian, German,
 * Portuguese, Arabic, Hebrew, Urdu, Hindi…). A sentence about a phase is
 * written whole twice and picked with `isHighRise` —
 * `isHighRise ? t("…HighRise", "Add a building or phase") : t("…LowRise", "Add a phase")` —
 * which is why there is no lower-case form here to slot in.
 * tests/buildingTermSentences.test.ts holds it.
 */
export function useBuildingTerm(projectIdOverride?: string) {
  const t = useTranslation();
  const isHighRise = useIsHighRise(projectIdOverride);

  return {
    isHighRise,
    /** Singular, title-cased — e.g. "Phase" or "Building/Phase" */
    singular: isHighRise ? t("invBuildingTermSingularHighRise", "Building/Phase") : t("invBuildingTermSingular", "Phase"),
    /** Plural, title-cased — e.g. "Phases" or "Buildings/Phases" */
    plural: isHighRise ? t("invBuildingTermPluralHighRise", "Buildings/Phases") : t("invBuildingTermPlural", "Phases"),
  };
}
